import { useCallback, useState } from 'react';

import { FetchService } from '../../../modules/cloud';
import { IntegrationService } from '../../../modules/integration';
import { useService } from '@toeverything/infra';

export interface DevtoUser {
  id: number;
  username: string;
  name: string;
  twitter_username: string | null;
  github_username: string | null;
  website_url: string | null;
  profile_image: string;
}

export interface DevtoArticle {
  id: number;
  title: string;
  description: string;
  slug: string;
  path: string;
  url: string;
  published: boolean;
  published_at: string | null;
  tag_list: string[];
  reading_time_minutes: number;
}

export interface PublishResult {
  articleId: number;
  articleUrl: string;
}

interface ErrorResponse {
  message?: string;
  error?: string;
}

export function useDevtoPublish() {
  const fetchService = useService(FetchService);
  const integrationService = useService(IntegrationService);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getApiKey = useCallback((): string | undefined => {
    return integrationService.devto.getApiKey();
  }, [integrationService]);

  const isConnected = useCallback((): boolean => {
    return integrationService.devto.isConnected();
  }, [integrationService]);

  const checkStatus = useCallback(async (): Promise<{ configured: boolean }> => {
    // Check if user has configured their API key locally
    return {
      configured: isConnected(),
    };
  }, [isConnected]);

  const getCurrentUser = useCallback(async (): Promise<DevtoUser> => {
    const apiKey = getApiKey();
    if (!apiKey) {
      throw new Error('Dev.to API key not configured');
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetchService.fetch('/api/devto/me', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ apiKey }),
      });

      if (!response.ok) {
        const errorData: ErrorResponse = await response.json().catch(() => ({}));
        const message =
          errorData.message ||
          errorData.error ||
          `Failed to fetch user (${response.status})`;
        throw new Error(message);
      }

      const data = await response.json();
      return data.user;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to fetch user';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchService, getApiKey]);

  const getMyArticles = useCallback(
    async (
      status?: 'all' | 'published' | 'unpublished'
    ): Promise<DevtoArticle[]> => {
      const apiKey = getApiKey();
      if (!apiKey) {
        throw new Error('Dev.to API key not configured');
      }

      setLoading(true);
      setError(null);
      try {
        const response = await fetchService.fetch('/api/devto/articles/list', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ apiKey, status }),
        });

        if (!response.ok) {
          const errorData: ErrorResponse = await response.json().catch(() => ({}));
          const message =
            errorData.message ||
            errorData.error ||
            `Failed to fetch articles (${response.status})`;
          throw new Error(message);
        }

        const data = await response.json();
        return data.articles || [];
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to fetch articles';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchService, getApiKey]
  );

  const publishArticle = useCallback(
    async (
      title: string,
      content: string,
      options?: {
        description?: string;
        tags?: string[];
        series?: string;
        mainImage?: string;
        canonicalUrl?: string;
        published?: boolean;
      }
    ): Promise<PublishResult> => {
      const apiKey = getApiKey();
      if (!apiKey) {
        throw new Error('Dev.to API key not configured. Please connect your Dev.to account in Settings > Integrations.');
      }

      setLoading(true);
      setError(null);
      try {
        const response = await fetchService.fetch('/api/devto/publish', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            apiKey,
            title,
            content,
            ...options,
          }),
        });

        if (!response.ok) {
          const errorData: ErrorResponse = await response.json().catch(() => ({}));
          const message =
            errorData.message ||
            errorData.error ||
            `Failed to publish (${response.status})`;
          throw new Error(message);
        }

        const data = await response.json();
        return data;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to publish';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchService, getApiKey]
  );

  const updateArticle = useCallback(
    async (
      articleId: number,
      options: {
        title?: string;
        content?: string;
        description?: string;
        tags?: string[];
        series?: string;
        mainImage?: string;
        canonicalUrl?: string;
        published?: boolean;
      }
    ): Promise<PublishResult> => {
      const apiKey = getApiKey();
      if (!apiKey) {
        throw new Error('Dev.to API key not configured');
      }

      setLoading(true);
      setError(null);
      try {
        const response = await fetchService.fetch(`/api/devto/articles/${articleId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ apiKey, ...options }),
        });

        if (!response.ok) {
          const errorData: ErrorResponse = await response.json().catch(() => ({}));
          const message =
            errorData.message ||
            errorData.error ||
            `Failed to update (${response.status})`;
          throw new Error(message);
        }

        const data = await response.json();
        return data;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to update';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchService, getApiKey]
  );

  return {
    checkStatus,
    getCurrentUser,
    getMyArticles,
    publishArticle,
    updateArticle,
    isConnected,
    getApiKey,
    loading,
    error,
  };
}
